import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { DailyCount, IOC, IOCBlacklistEntry, SherlockCounts, SherlockCustomer, SherlockMetric, Sighting } from "../models/SherlockModel"
import { SightedIOC } from "../models/ReportModel"

interface SherlockState {
    pagedIOCs: IOC[]
    pagedSightings: Sighting[]
    pageSize: number

    pagedSightingsEnd: boolean
    pagedIOCsEnd: boolean
    streamSightings: Sighting[]

    streamSightingsDisplay: boolean
    pagedSightingsDisplay: boolean
    pagedIOCsDisplay: boolean

    additionalIOCs: IOC[]
    additionalSightings: Sighting[]

    customers: SherlockCustomer[]
    totalCounts: SherlockCounts | null
    dailyMetrics: DailyCount[]
    weeklyMetrics: SherlockMetric[]
    lastWeekIOCsRank: Record<string, SightedIOC[]>
    lastMonthIOCsRank: Record<string, SightedIOC[]>
    iocBlacklist: IOCBlacklistEntry[]

    sherlockActiveTab: string

    iocTrendsWeek?: CustomerTrendMap;
    iocTrendsMonth?: CustomerTrendMap;
}

const initialState: SherlockState = {
    pagedIOCs: [],
    pagedSightings: [],
    // pageSize should be same as query limit in backend api
    pageSize: 25,

    pagedSightingsEnd: false,
    pagedIOCsEnd: false,
    streamSightings: [],

    streamSightingsDisplay: true,
    pagedSightingsDisplay: true,
    pagedIOCsDisplay: true,

    additionalIOCs: [],
    additionalSightings: [],

    customers: [],
    totalCounts: null,
    dailyMetrics: [],
    weeklyMetrics: [],
    lastWeekIOCsRank: {},
    lastMonthIOCsRank: {},
    iocBlacklist: [],

    sherlockActiveTab: "sightings",

    iocTrendsWeek: undefined,
    iocTrendsMonth: undefined
}

export const sherlockSlice = createSlice({
    name: 'sherlock',
    initialState,
    reducers: {
        setPagedSightings: (state, action) => {
            state.pagedSightings.push(...action.payload)
            state.pagedSightings.sort((a, b) => (a.date > b.date ? 1 : -1))
            if (action.payload.length < state.pageSize) {
                state.pagedSightingsEnd = true
            }
        },
        setPagedIOCs: (state, action) => {
            state.pagedIOCs.push(...action.payload)
            if (action.payload.length < state.pageSize) {
                state.pagedIOCsEnd = true
            }
        },
        setStreamSightings: (state, action) => {
            state.streamSightings.unshift(action.payload)
            // keep only 500 sightings in stream sightings
            if (state.streamSightings.length > 500) {
                state.streamSightings.pop()
            }
        },
        setStreamSightingsDisplay: (state, action) => {
            state.streamSightingsDisplay = action.payload
        },
        setPagedSightingsDisplay: (state, action) => {
            state.pagedSightingsDisplay = action.payload
        },
        setPagedIOCsDisplay: (state, action) => {
            state.pagedIOCsDisplay = action.payload
        },
        setAdditionalIOCs: (state, action) => {
            state.additionalIOCs.push(...action.payload)
        },
        setAdditionalSightings: (state, action) => {
            state.additionalIOCs.push(...action.payload)
        },
        setSherlockCustomers: (state, action) => {
            state.customers = action.payload
        },
        setSherlockTotalCounts: (state, action) => {
            state.totalCounts = action.payload
        },
        setSherlockDailyMetrics: (state, action) => {
            state.dailyMetrics = action.payload
        },
        setSherlockWeeklyMetrics: (state, action) => {
            state.weeklyMetrics = action.payload
        },
        setLastWeekIOCsRank: (state, action) => {
            state.lastWeekIOCsRank = action.payload
        },
        setLastMonthIOCsRank: (state, action) => {
            state.lastMonthIOCsRank = action.payload
        },
        setIOCBlacklist: (state, action) => {
            state.iocBlacklist = action.payload
        },
        setSherlockActiveTab: (state, action) => {
            state.sherlockActiveTab = action.payload
        },
        // Merge helper so we can incrementally add customers/indicators without wiping others
        mergeIocTrendsWeek(state, action: PayloadAction<CustomerTrendMap>) {
            state.iocTrendsWeek = mergeTrendMaps(state.iocTrendsWeek, action.payload);
        },
        mergeIocTrendsMonth(state, action: PayloadAction<CustomerTrendMap>) {
            state.iocTrendsMonth = mergeTrendMaps(state.iocTrendsMonth, action.payload);
        },
        resetIocTrends(state) {
            state.iocTrendsWeek = undefined;
            state.iocTrendsMonth = undefined;
        }
    }
})

function mergeTrendMaps(
    base?: CustomerTrendMap,
    incoming?: CustomerTrendMap
): CustomerTrendMap | undefined {
    if (!incoming) return base;
    const out: CustomerTrendMap = { ...(base || {}) };
    for (const [cust, map] of Object.entries(incoming)) {
        if (!out[cust]) out[cust] = {};
        for (const [ind, series] of Object.entries(map || {})) {
            out[cust][ind] = series; // replace or assign; could also dedupe by x here if needed
        }
    }
    return out;
}


export type IocTrendPoint = { x: string; y: number };
export type TrendSeriesMap = Record<string, IocTrendPoint[]>;         // indicator(b64 or decoded) -> series
export type CustomerTrendMap = Record<string, TrendSeriesMap>;        // customerId -> TrendSeriesMap

export const { setPagedSightings, setPagedIOCs, setStreamSightings,
    setStreamSightingsDisplay, setPagedSightingsDisplay, setPagedIOCsDisplay,
    setAdditionalIOCs, setAdditionalSightings,
    setSherlockCustomers, setSherlockTotalCounts, setSherlockDailyMetrics, setSherlockWeeklyMetrics,
    setLastWeekIOCsRank, setLastMonthIOCsRank, setIOCBlacklist, setSherlockActiveTab,
    mergeIocTrendsWeek, mergeIocTrendsMonth, resetIocTrends
} = sherlockSlice.actions

export default sherlockSlice.reducer